import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { filterAssets } from 'client/store/assetsSlice/assetsSlice';
import { setIsolatedViewsAssetCategory, setIsolatedViewsOwnerId } from 'client/store/mapSlice/mapSlice';
import { setSectorSpecificStyles } from 'client/store/layersSlice/layersSlice';
import {
    selectOwnerState,
    setSelectedAssetCategory as setSelectedAssetCategoryAction,
    setSelectedOwnerId
} from 'client/store/isolatedViewsSlice/isolatedViewsSlice';
import { syncOwnerPreferencesToMap } from '../OwnerPreferences';
import { ALL_CATEGORIES } from '../constants';

const useAssetOwnerFiltering = ({
    owner,
    hierarchy,
    selectedOwner,
    setSelectedAssetCategory,
    categoriesForOwner,
    effectiveSelectedAssetCategory
}) => {
    const dispatch = useDispatch();
    const ownerPreferences = useSelector(state => selectOwnerState(state, owner?.dataProviderId));

    const defaultOwnerFilterFunction = useCallback((feature) => {
        const attributeValue = feature?.get('dataproviderid_fk');
        return owner?.dataProviderId === attributeValue;
    }, [owner?.dataProviderId]);

    const filterAssetsById = useCallback((assetOwnerId, filterFunction, assetCategory = ALL_CATEGORIES, category = null) => {
        if (!assetOwnerId) {
            return;
        }

        dispatch(setSelectedOwnerId(assetOwnerId));
        dispatch(setIsolatedViewsOwnerId(assetOwnerId));
        dispatch(setSelectedAssetCategoryAction(category ? { id: assetCategory, category } : assetCategory));
        dispatch(setIsolatedViewsAssetCategory(assetCategory));
        dispatch(filterAssets({ filterFunction: filterFunction || defaultOwnerFilterFunction }));
    }, [defaultOwnerFilterFunction, dispatch]);

    const showRasterLayers = useCallback((visible) => {
        const nextHierarchy = hierarchy?.copy?.();
        if (!nextHierarchy) {
            return;
        }

        nextHierarchy.leaves().forEach(node => {
            if (node?.data?.sourceType === 'WMTS') {
                node.data = {
                    ...node.data,
                    checked: visible
                };
            }
        });
    }, [hierarchy]);

    const handleSelectAll = useCallback(() => {
        setSelectedAssetCategory(ALL_CATEGORIES);
        filterAssetsById(owner?.dataProviderId, defaultOwnerFilterFunction, ALL_CATEGORIES);
    }, [defaultOwnerFilterFunction, filterAssetsById, owner?.dataProviderId, setSelectedAssetCategory]);

    const handleChange = useCallback(({ id, assetTypes, isHiddenAssetsCategory, category }) => {
        setSelectedAssetCategory(id);

        if (isHiddenAssetsCategory) {
            filterAssetsById(owner?.dataProviderId, () => false, id, category);
            return;
        }

        filterAssetsById(owner?.dataProviderId, (feature) => {
            const attributeValue = feature?.get('dataproviderid_fk');
            const featureId = feature?.getId();
            const assetType = featureId?.split('.')?.[0];
            const featureIdIsInCategoryAssets = assetTypes.some(type => type === assetType);

            return owner?.dataProviderId === attributeValue && featureIdIsInCategoryAssets;
        }, id, category);
    }, [filterAssetsById, owner?.dataProviderId, setSelectedAssetCategory]);

    const handleAccordionHeading = useCallback((ownerId) => {
        if (selectedOwner === ownerId) {
            dispatch(setSelectedOwnerId(null));
            dispatch(setIsolatedViewsOwnerId(null));
            dispatch(setSelectedAssetCategoryAction(null));
            dispatch(setIsolatedViewsAssetCategory(null));
            dispatch(setSectorSpecificStyles({ enabled: false, sector: null }));
            dispatch(filterAssets());
            showRasterLayers(true);
            return;
        }

        dispatch(setSelectedOwnerId(ownerId));
        dispatch(setIsolatedViewsOwnerId(ownerId));
        syncOwnerPreferencesToMap(dispatch, ownerPreferences);

        if (effectiveSelectedAssetCategory === ALL_CATEGORIES) {
            filterAssetsById(ownerId, defaultOwnerFilterFunction, ALL_CATEGORIES);
            return;
        }

        const selectedCategoryObj = categoriesForOwner.find(category => category.id === effectiveSelectedAssetCategory);
        if (selectedCategoryObj) {
            handleChange(selectedCategoryObj);
        } else {
            filterAssetsById(ownerId);
        }
    }, [categoriesForOwner, defaultOwnerFilterFunction, dispatch, effectiveSelectedAssetCategory, filterAssetsById, handleChange, selectedOwner, showRasterLayers]);

    return {
        filterAssetsById,
        handleSelectAll,
        handleChange,
        handleAccordionHeading,
        showRasterLayers,
        defaultOwnerFilterFunction
    };
};

export default useAssetOwnerFiltering;
